/**
 * symbolPricingService — unified symbol resolution & real-time pricing.
 *
 * Maps user-facing tickers (SPX, ES, NQ, AAPL, etc.) to the correct:
 *   1. Polygon WS symbol for live price (ETFs proxy indexes/futures)
 *   2. GEX underlying for option chain (SPX→SPXW, NDX→NDXP, etc.)
 *   3. Futures scaling mode (indicates levels need ratio adjustment)
 *
 * Strategy: Polygon WS has Stocks Advanced+ → ETFs & stocks only.
 * Index charts use native OHLC (^GSPC, ^NDX, …) via /api/historical/equity-bars.
 * Futures (ES, NQ, etc.) still use ETF proxies + scaling when needed.
 */

// ── Core Symbol Map ────────────────────────────────────────────────────────

export interface SymbolMapping {
    /** User-facing display symbol */
    displaySymbol: string;
    /** Polygon WS subscription symbol (ETF/stock fallback when no index chart) */
    wsSymbol: string;
    /** Yahoo/provider ticker for index OHLC (e.g. ^GSPC) — real index chart levels */
    chartSymbol?: string;
    /** GEX option chain underlying (sent to backend) */
    gexUnderlying: string;
    /** Label for chart price axis */
    priceSource: string;
    /** True if this is a futures contract needing price scaling */
    isFutures: boolean;
}

const SYMBOL_MAP: Record<string, SymbolMapping> = {
    // ── Indexes → native chart (^GSPC) + SPXW option chain ─────────────
    SPX: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    ".SPX": {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    $SPX: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    SP500: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    SPX500: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    "SPX 500": {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    GSPC: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    "^GSPC": {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    "^SPX": {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },

    NDX: {
        displaySymbol: "NDX",
        chartSymbol: "^NDX",
        wsSymbol: "QQQ",
        gexUnderlying: "NDXP",
        priceSource: "NDX",
        isFutures: false,
    },
    NDXP: {
        displaySymbol: "NDX",
        chartSymbol: "^NDX",
        wsSymbol: "QQQ",
        gexUnderlying: "NDXP",
        priceSource: "NDX",
        isFutures: false,
    },
    NDQ: {
        displaySymbol: "NDX",
        chartSymbol: "^NDX",
        wsSymbol: "QQQ",
        gexUnderlying: "NDXP",
        priceSource: "NDX",
        isFutures: false,
    },

    SPXW: {
        displaySymbol: "SPXW",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPX",
        isFutures: false,
    },
    SPXQ: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXQ",
        priceSource: "SPX",
        isFutures: false,
    },
    SPXPM: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "SPXPM",
        priceSource: "SPX",
        isFutures: false,
    },
    XSP: {
        displaySymbol: "SPX",
        chartSymbol: "^GSPC",
        wsSymbol: "SPY",
        gexUnderlying: "XSP",
        priceSource: "SPX",
        isFutures: false,
    },

    RUT: {
        displaySymbol: "RUT",
        chartSymbol: "^RUT",
        wsSymbol: "IWM",
        gexUnderlying: "RUTW",
        priceSource: "RUT",
        isFutures: false,
    },
    RUTW: {
        displaySymbol: "RUT",
        chartSymbol: "^RUT",
        wsSymbol: "IWM",
        gexUnderlying: "RUTW",
        priceSource: "RUT",
        isFutures: false,
    },
    RUTQ: {
        displaySymbol: "RUT",
        chartSymbol: "^RUT",
        wsSymbol: "IWM",
        gexUnderlying: "RUTQ",
        priceSource: "RUT",
        isFutures: false,
    },

    VIX: {
        displaySymbol: "VIX",
        chartSymbol: "^VIX",
        wsSymbol: "VXX",
        gexUnderlying: "VIX",
        priceSource: "VIX",
        isFutures: false,
    },
    VIXW: {
        displaySymbol: "VIX",
        chartSymbol: "^VIX",
        wsSymbol: "VXX",
        gexUnderlying: "VIXW",
        priceSource: "VIX",
        isFutures: false,
    },
    "/VX": {
        displaySymbol: "VIX",
        chartSymbol: "^VIX",
        wsSymbol: "VXX",
        gexUnderlying: "VIX",
        priceSource: "VIX",
        isFutures: false,
    },

    // ── Futures → ETF proxies + scaling flag ─────────────────────────────
    ES: {
        displaySymbol: "ES",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPY",
        isFutures: true,
    },
    "/ES": {
        displaySymbol: "ES",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPY",
        isFutures: true,
    },
    "ES1!": {
        displaySymbol: "ES",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPY",
        isFutures: true,
    },
    MES: {
        displaySymbol: "MES",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPY",
        isFutures: true,
    },
    "/MES": {
        displaySymbol: "MES",
        wsSymbol: "SPY",
        gexUnderlying: "SPXW",
        priceSource: "SPY",
        isFutures: true,
    },

    NQ: {
        displaySymbol: "NQ",
        wsSymbol: "QQQ",
        gexUnderlying: "NDXP",
        priceSource: "QQQ",
        isFutures: true,
    },
    "/NQ": {
        displaySymbol: "NQ",
        wsSymbol: "QQQ",
        gexUnderlying: "NDXP",
        priceSource: "QQQ",
        isFutures: true,
    },
    "NQ1!": {
        displaySymbol: "NQ",
        wsSymbol: "QQQ",
        gexUnderlying: "NDXP",
        priceSource: "QQQ",
        isFutures: true,
    },
    MNQ: {
        displaySymbol: "MNQ",
        wsSymbol: "QQQ",
        gexUnderlying: "NDXP",
        priceSource: "QQQ",
        isFutures: true,
    },

    RTY: {
        displaySymbol: "RTY",
        wsSymbol: "IWM",
        gexUnderlying: "RUTW",
        priceSource: "IWM",
        isFutures: true,
    },
    "/RTY": {
        displaySymbol: "RTY",
        wsSymbol: "IWM",
        gexUnderlying: "RUTW",
        priceSource: "IWM",
        isFutures: true,
    },
    "RTY1!": {
        displaySymbol: "RTY",
        wsSymbol: "IWM",
        gexUnderlying: "RUTW",
        priceSource: "IWM",
        isFutures: true,
    },

    // ── Commodity futures ────────────────────────────────────────────────
    GC: {
        displaySymbol: "GC",
        wsSymbol: "GLD",
        gexUnderlying: "GC",
        priceSource: "GLD",
        isFutures: true,
    },
    "/GC": {
        displaySymbol: "GC",
        wsSymbol: "GLD",
        gexUnderlying: "GC",
        priceSource: "GLD",
        isFutures: true,
    },
    "GC1!": {
        displaySymbol: "GC",
        wsSymbol: "GLD",
        gexUnderlying: "GC",
        priceSource: "GLD",
        isFutures: true,
    },
    GLD: {
        displaySymbol: "GLD",
        wsSymbol: "GLD",
        gexUnderlying: "GC",
        priceSource: "GLD",
        isFutures: false,
    },
    MGC: {
        displaySymbol: "MGC",
        wsSymbol: "GLD",
        gexUnderlying: "GC",
        priceSource: "GLD",
        isFutures: true,
    },

    CL: {
        displaySymbol: "CL",
        wsSymbol: "USO",
        gexUnderlying: "CL",
        priceSource: "USO",
        isFutures: true,
    },
    "/CL": {
        displaySymbol: "CL",
        wsSymbol: "USO",
        gexUnderlying: "CL",
        priceSource: "USO",
        isFutures: true,
    },
    "CL1!": {
        displaySymbol: "CL",
        wsSymbol: "USO",
        gexUnderlying: "CL",
        priceSource: "USO",
        isFutures: true,
    },
    USO: {
        displaySymbol: "USO",
        wsSymbol: "USO",
        gexUnderlying: "CL",
        priceSource: "USO",
        isFutures: false,
    },
};

/** Option-chain roots to try (order matters) when fetching GEX for index products. */
const INDEX_GEX_CHAIN_CANDIDATES: Record<string, string[]> = {
    SPX: ["SPXW", "SPX", "^SPX"],
    SPXW: ["SPXW", "SPX", "^SPX"],
    NDX: ["NDXP", "NDX", "^NDX"],
    NDXP: ["NDXP", "NDX", "^NDX"],
    RUT: ["RUTW", "RUT", "^RUT"],
    RUTW: ["RUTW", "RUT", "^RUT"],
    VIX: ["VIXW", "VIX"],
    VIXW: ["VIXW", "VIX"],
};

function normalizeRawTicker(raw: string): string {
    return String(raw || "")
        .toUpperCase()
        .replace(/^\$/, "")
        .replace(/^I:/, "")
        .trim();
}

/**
 * Resolve a raw user input symbol into its canonical mapping.
 * Handles: TradingView exchange prefixes, futures ! suffixes, misc formatting.
 */
export function resolveSymbol(raw: string): SymbolMapping | null {
    let s = String(raw || "")
        .toUpperCase()
        .replace(/^\$/, "")
        .trim();

    // Strip TradingView exchange prefixes: CME_MINI:ES1 → ES1, COMEX:GC1 → GC1
    s = s.replace(/^[A-Z_]+:/, "");
    // Strip exchange underscore prefixes: COMEX_GC1 → GC1
    for (const ex of [
        "CME_MINI",
        "CME",
        "COMEX",
        "NYMEX",
        "CBOT",
        "ICE",
        "EUREX",
        "CBOE",
    ]) {
        if (s.startsWith(ex + "_")) {
            s = s.slice(ex.length + 1);
            break;
        }
    }
    // Split remaining underscores, take last meaningful part
    if (s.includes("_")) {
        const parts = s.split("_").filter(Boolean);
        for (let i = parts.length - 1; i >= 0; i--) {
            const test = parts[i];
            if (SYMBOL_MAP[test]) {
                s = test;
                break;
            }
        }
        if (s.includes("_")) s = parts.filter(Boolean).pop() || s;
    }

    // Direct lookup
    if (SYMBOL_MAP[s]) return SYMBOL_MAP[s];

    // SPX 500 / S&P style aliases
    if (/^SPX\s*500$|^SP\s*500$|^S&P\s*500$/.test(s.replace(/\s+/g, " ")))
        return SYMBOL_MAP.SPX;

    // Try adding ! for futures continuous contracts
    if (/^[A-Z]{1,5}\d$/.test(s)) {
        const withBang = s + "!";
        if (SYMBOL_MAP[withBang]) return SYMBOL_MAP[withBang];
    }

    // If it looks like a stock/ETF ticker (1-5 uppercase letters), treat as-is
    if (/^[A-Z]{1,5}$/.test(s)) {
        return {
            displaySymbol: s,
            wsSymbol: s,
            gexUnderlying: s,
            priceSource: s,
            isFutures: false,
        };
    }

    return null;
}

/**
 * Get the Polygon WS subscription symbol for a user input.
 * Returns the ETF/stock ticker to subscribe to via WebSocket.
 */
export function getWsSymbol(raw: string): string {
    const resolved = resolveSymbol(raw);
    return resolved?.wsSymbol || raw.toUpperCase();
}

/** Chart OHLC ticker (index ^GSPC or equity ticker). */
export function getChartSymbol(raw: string): string {
    const resolved = resolveSymbol(raw);
    if (resolved?.chartSymbol) return resolved.chartSymbol;
    return resolved?.wsSymbol || normalizeRawTicker(raw);
}

const POLYGON_INDEX_TICKER: Record<string, string> = {
    SPX: "I:SPX",
    SPXW: "I:SPX",
    NDX: "I:NDX",
    NDXP: "I:NDX",
    RUT: "I:RUT",
    RUTW: "I:RUT",
    VIX: "I:VIX",
    VIXW: "I:VIX",
};

/** Polygon indices API ticker when subscription allows (I:SPX, I:NDX, …). */
export function getPolygonIndexSymbol(raw: string): string | null {
    const resolved = resolveSymbol(raw);
    const key = resolved?.displaySymbol || normalizeRawTicker(raw);
    return POLYGON_INDEX_TICKER[key] || null;
}

export function usesNativeIndexChart(
    resolved: SymbolMapping | null,
): boolean {
    return Boolean(resolved?.chartSymbol);
}

/** ETF-scaled chart (futures only); indexes use native chartSymbol. */
export function isIndexEtfProxy(resolved: SymbolMapping | null): boolean {
    if (!resolved || resolved.isFutures) return false;
    if (resolved.chartSymbol) return false;
    return resolved.wsSymbol !== resolved.gexUnderlying;
}

/**
 * Scale factor to align ETF-proxy chart prices with index option strikes.
 * Returns 1 when chain spot or live ETF price is unavailable.
 */
export function indexChartScale(
    resolved: SymbolMapping | null,
    chainSpot: number,
    etfLivePrice: number,
): number {
    if (!isIndexEtfProxy(resolved) || chainSpot <= 0 || etfLivePrice <= 0) {
        return 1;
    }
    const ratio = chainSpot / etfLivePrice;
    if (ratio > 1.05 || ratio < 0.95) return ratio;
    return 1;
}

/**
 * Get the GEX option chain underlying for backend queries.
 */
export function getGexUnderlying(raw: string): string {
    const resolved = resolveSymbol(raw);
    return resolved?.gexUnderlying || normalizeRawTicker(raw);
}

/**
 * Underlying symbols to query for option chain / GEX (tries each until data returns).
 */
export function getGexChainCandidates(raw: string): string[] {
    const resolved = resolveSymbol(raw);
    const primary =
        resolved?.gexUnderlying || normalizeRawTicker(raw);
    const key = resolved?.displaySymbol || primary;
    const family = INDEX_GEX_CHAIN_CANDIDATES[key] || INDEX_GEX_CHAIN_CANDIDATES[primary] || [];
    const out: string[] = [];
    const add = (sym: string) => {
        const u = normalizeRawTicker(sym);
        if (u && !out.includes(u)) out.push(u);
    };
    add(primary);
    family.forEach(add);
    return out;
}

export function isIndexGexSymbol(raw: string): boolean {
    const resolved = resolveSymbol(raw);
    return Boolean(
        resolved &&
            (resolved.chartSymbol ||
                resolved.wsSymbol !== resolved.gexUnderlying),
    );
}

/**
 * Check if symbol is a futures contract that needs GEX level scaling.
 */
export function isFuturesContract(raw: string): boolean {
    const resolved = resolveSymbol(raw);
    return resolved?.isFutures || false;
}

/**
 * For display: return a human-readable label showing the price source.
 * e.g., "SPX → via SPY" or "ES → via SPY (futures scale)"
 */
export function getPriceSourceLabel(raw: string): string {
    const resolved = resolveSymbol(raw);
    if (!resolved) return raw;
    if (resolved.displaySymbol === resolved.priceSource)
        return resolved.displaySymbol;
    if (resolved.chartSymbol) {
        return `${resolved.displaySymbol} chart · chain ${resolved.gexUnderlying}`;
    }
    if (isIndexEtfProxy(resolved)) {
        return `${resolved.displaySymbol} → ${resolved.priceSource} (scaled)`;
    }
    const suffix = resolved.isFutures ? " (futures scale)" : "";
    return `${resolved.displaySymbol} → ${resolved.priceSource}${suffix}`;
}
